import { useMemo } from "react";
import { Lock } from "lucide-react";
import { CHART_TYPES } from "@/lib/NseChartEngine";

const CHART_LIST = Object.values(CHART_TYPES);
const TIER_W = { FREE: 1, ADVANCED: 2, EXPERT: 3 };
const DAYS_BY_TIER = { FREE: 30, ADVANCED: 180, EXPERT: 365 };

export const INDICATOR_LIST = [
    { key: "ma",        label: "MA (20)" },
    { key: "ema",       label: "EMA (20)" },
    { key: "bollinger", label: "Bollinger Bands" },
    { key: "rsi",       label: "RSI (14)" },
    { key: "macd",      label: "MACD (12,26,9)" },
];

/**
 * ChartControls — chart-type picker + indicator toggles + window-size picker.
 * Purely presentational; parent owns state.
 */
export default function ChartControls({ tier, chartType, days, indicators, onPickType, onPickDays, onToggleIndicator }) {
    const tierWeight = TIER_W[tier] || 1;
    const cap = DAYS_BY_TIER[tier] || 30;

    const buttons = useMemo(() => CHART_LIST.map((c) => ({
        ...c,
        unlocked: tierWeight >= (TIER_W[c.tier] || 1),
    })), [tierWeight]);

    return (
        <div className="space-y-2 mb-3">
        <div className="flex flex-wrap items-center gap-2">
            <span className="overline mr-1">Chart type</span>
            {buttons.map((c) => {
                const active = c.id === chartType;
                const cls = active
                    ? "border-primary bg-primary/10 text-primary"
                    : c.unlocked
                        ? "border-border hover:border-primary/50 hover:bg-primary/5"
                        : "border-border/60 text-muted-foreground opacity-70 hover:opacity-100";
                return (
                    <button
                        key={c.id}
                        type="button"
                        onClick={() => onPickType(c)}
                        title={c.unlocked ? c.name : `${c.name} — Institution feature`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${cls}`}
                        data-testid={`chart-type-${c.id.toLowerCase()}`}
                    >
                        {!c.unlocked && <Lock className="w-3 h-3" />}
                        {c.name}
                        {!c.unlocked && (
                            <span
                                className="ml-1 text-[9px] font-extrabold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30"
                                data-testid={`chart-type-${c.id.toLowerCase()}-institution-pill`}
                            >
                                Institution
                            </span>
                        )}
                    </button>
                );
            })}

            <div className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                {onPickDays && (
                <>
                <span className="overline">Window</span>
                {[30, 90, 180, 365].map((d) => {
                    const disabled = d > cap;
                    const cls = days === d ? "border-primary text-primary bg-primary/10" : "border-border";
                    const dis = disabled ? " opacity-40 cursor-not-allowed" : " hover:border-primary/50";
                    return (
                        <button
                            key={d}
                            type="button"
                            disabled={disabled}
                            onClick={() => onPickDays(d)}
                            className={`px-2 py-0.5 rounded border text-xs ${cls}${dis}`}
                            data-testid={`chart-window-${d}`}
                        >
                            {d}d
                        </button>
                    );
                })}
                </>
                )}
            </div>
        </div>

        {onToggleIndicator && (
            <div className="flex flex-wrap items-center gap-2">
                <span className="overline mr-1">Indicators</span>
                {INDICATOR_LIST.map((ind) => {
                    const active = !!indicators?.[ind.key]?.enabled;
                    return (
                        <button
                            key={ind.key}
                            type="button"
                            onClick={() => onToggleIndicator(ind.key)}
                            className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-colors ${
                                active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50 hover:bg-primary/5"
                            }`}
                            data-testid={`chart-indicator-${ind.key}`}
                        >
                            {ind.label}
                        </button>
                    );
                })}
            </div>
        )}
        </div>
    );
}
